// Copyright ©️ 2026 eVolve MEP, LLC

import { PART_TYPE_ASSEMBLY_ID, type WithOptional, type WorkOrderItem } from '@evolvemep/api-ts';
import { Card } from '@mantine/core';

import { EvolveIcon } from 'assets/icons/EvolveIcon';
import { isNotNil } from 'helpers/isNotNil';
import { useIsMobile } from 'hooks/useIsMobile';
import { SPOOL_UOM_ID } from 'modules/Shop/WorkOrders/WorkOrderDetail/constants';

// TODO: Shipment items do not take fabrication elements into account because this was developed before that feature, which is why we have "WithOptional" here.
// The correct fix is probably to add that feature on the BE and remove this `WithOptional`.
type Props = {
  item: WithOptional<
    Pick<WorkOrderItem, 'hasFabricationElements' | 'unitOfMeasureId' | 'partId' | 'partTypeId'>,
    'hasFabricationElements'
  >;
};

const pickItemTypeIcon = ({ item: { hasFabricationElements, unitOfMeasureId, partId, partTypeId } }: Props) => {
  if (unitOfMeasureId === SPOOL_UOM_ID) return 'Spools';
  if (hasFabricationElements) return 'Fabrication';
  if (isNotNil(partId)) {
    if (partTypeId === PART_TYPE_ASSEMBLY_ID) return 'Assembly';
    return 'Part';
  }
  return 'WriteInItem';
};

/**
 * Small inline rendering of {@link pickItemTypeIcon}. Use the helper directly when you need
 * a custom wrapper (e.g., the dark-card treatment in `SelectableItemCard`).
 */
export const WorkOrderItemTypeIcon = (props: Props) => {
  const isMobile = useIsMobile();
  return (
    <Card withBorder radius={4} p={isMobile ? 'xs' : 'sm'} flex="0 0 auto" bg="gray.8" c="gray.2">
      <EvolveIcon icon={pickItemTypeIcon(props)} />
    </Card>
  );
};
